/* eslint-disable @typescript-eslint/no-explicit-any */
/** This file is taken from orbiter. We copied it here as part of the migration. We would like to deprecate the usage of this going forward  */
import { isEmptyArray, isFunction, isNil, isString, isUndefined, mergeProps } from "@hopper-ui/components";
import {
    Children,
    type ComponentType,
    type ReactElement,
    type ReactNode,
    type Ref,
    type RefAttributes,
    cloneElement,
    useMemo
} from "react";
import { isFragment } from "react-is";

const SlotKey = "__slot__";

export interface SlotableType {
    [SlotKey]?: string;
}

function slotDecorator<P>(slotName: string, ElementType: P) {
    (ElementType as Record<string, unknown>)[SlotKey] = slotName;

    return ElementType;
}

export { slotDecorator as slot };

function getSlotKey(element: ReactElement) {
    return (
        (element.props && (element.props as any)["slot"]) ?? (element.type && (element.type as SlotableType)[SlotKey])
    );
}

type SlotElements = Record<string, ReactElement>;

function findSlots(children: ReactNode, slots: string[]): SlotElements {
    return (Children.toArray(children) as ReactElement[]).reduce((acc, x) => {
        if (!isNil(x)) {
            const slotKey = getSlotKey(x);

            if (!isNil(slotKey) && slots.includes(slotKey)) {
                acc[slotKey] = x;
            }
        }

        return acc;
    }, {} as SlotElements);
}

export interface SlotOptions {
    _: {
        defaultWrapper?: ComponentType<any>;
        required?: string[] | ((slotElements: SlotElements) => string[]);
    };
}

type GetSlotsReturn<T extends SlotOptions> = Partial<Record<keyof Omit<T, "_">, ReactElement>>;

function getSlots<T extends SlotOptions>(children: ReactNode, { _, ...slots }: T): GetSlotsReturn<T> {
    if (isNil(children)) {
        return {};
    }

    let slotElements: SlotElements = {};

    const resolvedChildren = resolveChildren(children);

    if (!isString(resolvedChildren)) {
        slotElements = findSlots(resolvedChildren, Object.keys(slots));
    }

    const { defaultWrapper: Wrapper, required } = _;

    if (!isNil(required)) {
        let unfulfilledSlots: string[] = [];

        if (isFunction(required)) {
            unfulfilledSlots = required(slotElements);
        } else {
            required.forEach(x => {
                if (isUndefined(slotElements[x])) {
                    unfulfilledSlots.push(x);
                }
            });
        }

        if (!isNil(unfulfilledSlots) && unfulfilledSlots.length !== 0) {
            throw new Error(
                `Required slot${unfulfilledSlots.length > 1 ? "s" : ""} ${unfulfilledSlots.map(x => `"${x}"`).join(", ")} must receive a component.`
            );
        }
    }

    if (!isNil(Wrapper)) {
        if (isEmptyArray(Object.keys(slotElements)) && !isNil(children)) {
            const wrapperSlot = (Wrapper as SlotableType)[SlotKey];

            if (isNil(wrapperSlot)) {
                throw new Error("A default wrapper must have a slot key.");
            }

            slotElements[wrapperSlot] = <Wrapper>{children}</Wrapper>;
        }
    }

    Object.keys(slotElements).forEach(x => {
        let slotProps = (slots as Record<string, any>)[x];

        if (isFunction(slotProps)) {
            slotProps = slotProps(slotElements[x], slotElements);
        }

        if (!isNil(slotProps)) {
            slotElements[x] = augmentElement(slotElements[x] as ReactElement, slotProps);
        }
    });

    return slotElements as GetSlotsReturn<T>;
}

type UseSlotsReturn<T extends SlotOptions> = GetSlotsReturn<T>;

/**
 * @deprecated Avoid using this utility going forward. Prefer using context to pass data to child components.
 */
export function useSlots<T extends SlotOptions>(children: ReactNode, slots: T): UseSlotsReturn<T> {
    return useMemo(() => getSlots(children, slots), [children, slots]);
}

function augmentElement(element: ReactElement & RefAttributes<any>, newProps: Record<string, any>) {
    const augmentedProps = mergeProps({ ...(element.props as any), ref: getElementRef(element) }, newProps);

    return cloneElement(element, augmentedProps);
}

function getElementRef(element: ReactElement): Ref<any> {
    return Object.prototype.propertyIsEnumerable.call(element.props as any, "ref")
        ? (element.props as any).ref
        : (element as any).ref;
}

// Support first level fragment: https://github.com/facebook/react/issues/11859.
function resolveFragment(children: ReactNode): ReactNode {
    if (isFragment(children)) {
        return (children as any).props.children;
    }

    return children;
}

function resolveChildren(children: ReactNode, renderProps?: Record<string, any>) {
    if (isFunction(children)) {
        return resolveFragment(children(renderProps));
    }

    return resolveFragment(children);
}
